'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';

import { useAuth, useSignInHandlers } from '@/hooks';

const REFERRAL_STORAGE_KEY = 'pm_referral_code';
const REFERRAL_EXPIRY_DAYS = 30;

/**
 * ReferralTracker Component
 * Headless component that listens for referral codes in the URL and persists them.
 * Supported params: 'r', 'referral', 'ref'
 */
export function ReferralTracker() {
  const searchParams = useSearchParams();
  const { isAuthenticated, isLoading } = useAuth();
  const triggerRef = useRef<string | null>(null);
  const timersRef = useRef<{ login?: NodeJS.Timeout; reset?: NodeJS.Timeout }>({});

  const { onLogin } = useSignInHandlers((error) => {
    if (error) console.warn('[ReferralTracker] Auto-login error handled:', error);
  });

  const processReferral = useCallback(() => {
    if (!searchParams) return;

    const r = searchParams.get('r');
    const referral = searchParams.get('referral');
    const ref = searchParams.get('ref');

    const code = r || referral || ref;

    // Security & Format Validation: Ensure code is safe and valid
    if (code && typeof code === 'string' && triggerRef.current !== code) {
      const VALID_CODE_REGEX = /^[a-zA-Z0-9_-]+$/;

      if (code.length > 50 || !VALID_CODE_REGEX.test(code)) {
        console.warn(
          '[ReferralTracker] Discarding invalid or potentially malicious referral code:',
          code
        );
        return;
      }

      triggerRef.current = code;

      // Clean up the URL parameters immediately for all users to maintain a clean address bar
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('r');
        url.searchParams.delete('referral');
        url.searchParams.delete('ref');
        window.history.replaceState({}, '', url.toString());
      } catch (e) {
        console.warn('[ReferralTracker] URL cleanup failed:', e);
      }

      // If the user is already logged in, we do not process the referral further.
      // This ensures no UI side-effects or storage updates occur for active sessions.
      if (isAuthenticated && !isLoading) {
        return;
      }

      try {
        // Save to localStorage with a timestamp for expiration checking
        const referralData = {
          code,
          timestamp: Date.now(),
          expiresAt: Date.now() + REFERRAL_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
        };

        localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(referralData));

        // Automatically open login if not already logged in
        if (!isLoading && !isAuthenticated) {
          timersRef.current.login = setTimeout(() => {
            onLogin();
          }, 1500);
        }
      } catch (err) {
        console.error('[ReferralTracker] Failed to persist referral code:', err);
      } finally {
        // Reset trigger after a delay to allow re-processing if they click again later
        timersRef.current.reset = setTimeout(() => {
          triggerRef.current = null;
        }, 10000);
      }
    }
  }, [searchParams, isAuthenticated, isLoading, onLogin]);

  useEffect(() => {
    // Stage 1: Persistence
    // We process the code as soon as it's seen in the URL
    const r = searchParams.get('r');
    const referral = searchParams.get('referral');
    const ref = searchParams.get('ref');
    const code = r || referral || ref;

    if (code && typeof code === 'string' && triggerRef.current !== code) {
      const VALID_CODE_REGEX = /^[a-zA-Z0-9_-]+$/;
      if (code.length <= 50 && VALID_CODE_REGEX.test(code)) {
        triggerRef.current = code;

        // Persist immediately
        const referralData = {
          code,
          timestamp: Date.now(),
          expiresAt: Date.now() + REFERRAL_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
        };
        localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(referralData));

        // Clean up URL
        try {
          const url = new URL(window.location.href);
          ['r', 'referral', 'ref'].forEach((p) => url.searchParams.delete(p));
          window.history.replaceState({}, '', url.toString());
        } catch (e) {
          console.error(e);
        }

        // Reset trigger ref after delay
        timersRef.current.reset = setTimeout(() => {
          triggerRef.current = null;
        }, 10000);
      }
    }

    // Stage 2: Login Trigger
    // We only trigger if NOT loading, NOT authenticated, and a code was JUST captured
    // or is already persisted but we haven't triggered this session.
    if (!isLoading && !isAuthenticated && code) {
      if (timersRef.current.login) clearTimeout(timersRef.current.login);
      timersRef.current.login = setTimeout(() => {
        onLogin();
      }, 1500);
    }

    return () => {
      if (timersRef.current.login) clearTimeout(timersRef.current.login);
      if (timersRef.current.reset) clearTimeout(timersRef.current.reset);
    };
  }, [searchParams, isAuthenticated, isLoading, onLogin]);

  return null;
}

/**
 * Utility to get the current valid referral code
 */
export function getPersistedReferralCode(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = localStorage.getItem(REFERRAL_STORAGE_KEY);
    if (!raw) return null;

    const data = JSON.parse(raw);

    // Structural validation
    if (!data || typeof data !== 'object' || !data.code) {
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
      return null;
    }

    // Expiry check
    if (data.expiresAt && Date.now() > data.expiresAt) {
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
      return null;
    }
    return data.code;
  } catch (e) {
    console.error('[ReferralTracker] Error reading referral code:', e);
    return null;
  }
}

/**
 * Utility to clear the referral code after successful signup
 */
export function clearPersistedReferralCode() {
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
    } catch (e) {
      console.error('[ReferralTracker] Error clearing referral code:', e);
    }
  }
}
